import { AgGridAngular } from "@ag-grid-community/angular";
import {
  CellClassParams,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { CurrencyPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { RouterModule } from "@angular/router";
import { AccountMarketplace, InventoryRule, Marketplace, Strategy, StrategyStateEnum } from "@front/m19-api-client";
import { gridConfig, restoreGridState, SIDE_BAR_NO_PIVOT } from "@front/m19-grid-config";
import {
  AdStatsEx,
  InventoryRules,
  InventoryStats,
  RuleExecutionResult,
  RuleExecutionResultEnum,
} from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  AuthService,
  InventoryService,
  SbStrategyService,
  SdStrategyService,
  SpStrategyService,
  UserSelectionService,
} from "@front/m19-services";
import {
  IAlertComponent,
  IButtonDirective,
  IIconComponent,
  ITooltipDirective,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { activeStrategiesColumn, ImageColumn } from "@m19-board/grid-config/grid-columns";
import { restoreDefaultColumns } from "@m19-board/grid-config/grid-config";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import {
  FBM_STOCK,
  FBM_STOCK_VALUE,
  FULFILLABLE_STOCK,
  FULFILLABLE_STOCK_VALUE,
  INBOUND_STOCK,
  INBOUND_STOCK_VALUE,
  ORDERS_30D,
  ORDERS_7D,
  RESERVED_STOCK,
  RESERVED_STOCK_VALUE,
  UNFULFILLABLE_STOCK,
  UNFULFILLABLE_STOCK_VALUE,
} from "@m19-board/models/MetricsDef";
import { AsinLinkComponent } from "@m19-board/product-view/asin-link.component";
import {
  constantField,
  CsvExportService,
  fieldExtractor,
  metricField,
  simpleField,
} from "@m19-board/services/csv-export.service";
import { CompactFilterComponent, Filter } from "@m19-board/shared/compact-filter/compact-filter.component";
import { AsinFilter, AsinFilterService } from "@m19-board/shared/filter/asin-filter.service";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, filter, map, Observable, switchMap, take, tap } from "rxjs";
import {
  AsinInventoryRuleModalComponent,
  AsinInventoryRulesModalData,
  AsinInventoryRulesOutput,
} from "../inventory-rules/asin-inventory-rules-modal.component";
import { EstimatedDaysRendererComponent } from "./estimated-days-renderer.component";
import { PauseAdvRendererComponent } from "./pause-adv-renderer.component";
import { ProductThumbnailGridComponent } from "@m19-board/product-view/product-thumbnail-grid.component";

@UntilDestroy()
@Component({
  selector: "app-seller-inventory",
  templateUrl: "./seller-inventory.component.html",
  providers: [AsinFilterService, CurrencyPipe],
  imports: [
    IButtonDirective,
    ITooltipDirective,
    ExportButtonComponent,
    AgGridAngular,
    TranslocoDirective,
    RouterModule,
    CompactFilterComponent,
    IIconComponent,
    IAlertComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SellerInventoryComponent implements OnInit {
  private readonly asinFilterService = inject(AsinFilterService);
  private readonly gridConfigService = inject(GridConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private translocoService = inject(TranslocoService);
  private asinService = inject(AsinService);
  private modalService = inject(ModalService);
  private toasterService = inject(ToasterService);
  private authService = inject(AuthService);
  private userSelectionService = inject(UserSelectionService);
  private accountSelectionService = inject(AccountSelectionService);
  private csvExportService = inject(CsvExportService);
  private spStrategyService = inject(SpStrategyService);
  private sbStrategyService = inject(SbStrategyService);
  private sdStrategyService = inject(SdStrategyService);
  private currencyPipe = inject(CurrencyPipe);
  private inventoryService = inject(InventoryService);

  metric: ColDef[] = this.gridConfigService
    .getMetricsColDef([
      ORDERS_7D,
      ORDERS_30D,
      FULFILLABLE_STOCK,
      FBM_STOCK,
      INBOUND_STOCK,
      RESERVED_STOCK,
      UNFULFILLABLE_STOCK,
      FULFILLABLE_STOCK_VALUE,
      FBM_STOCK_VALUE,
      INBOUND_STOCK_VALUE,
      RESERVED_STOCK_VALUE,
      UNFULFILLABLE_STOCK_VALUE,
    ])
    .map(({ headerName, ...def }: ColDef) => ({
      ...def,
      hide: def.colId?.includes("VALUE") || def.colId === ORDERS_7D.id,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      filterParams: {
        defaultOption: "greaterThan",
      },
      cellRendererParams: (params: ICellRendererParams<AdStatsEx>) => {
        return {
          ...def.cellRendererParams(params),
          previousData: undefined,
          currency: this.currency(),
          locale: this.locale(),
        };
      },
    }));

  filters = toSignal<Filter<AsinFilter>[]>(
    this.asinFilterService.getAsinFilters(false).pipe(
      filter((filter) => filter.length > 0),
      take(1),
    ),
  );

  onlyLowInventory = false;
  readonly lowInventoryNumber = signal(0);
  readonly lowInventoryPaused = signal(0);
  readonly dataSource = signal<InventoryStats[]>([]);

  gridApi?: GridApi;

  accountGroupName: string = "";
  strategies: Map<string, Strategy[]> = new Map();
  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { initialValue: false });
  locale = toSignal(this.authService.loggedUser$.pipe(map((u) => u.locale)), { initialValue: "" });
  currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  accountId!: string;
  marketplace!: Marketplace;
  inventoryRules: InventoryRules = new InventoryRules([]);
  readonly isManualManaging = signal(false);

  colDef: ColDef[] = [
    {
      ...ImageColumn,
      headerName: this.translocoService.translate("catalog-page.image", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("catalog-page.image"),
      pinned: "left",
      cellRendererSelector: (params) => {
        return {
          component: ProductThumbnailGridComponent,
          params: {
            marketplace: this.marketplace,
            asin: params.data.asin,
            smallImg: true,
            customSizeClass: "size-16",
          },
        };
      },
    },
    {
      headerValueGetter: () => this.translocoService.translate("common.product"),
      field: "asin",
      pinned: "left",
      cellRendererSelector: (params: any) => {
        return {
          component: AsinLinkComponent,
          params: {
            marketplace: this.marketplace,
            asin: params.data.asin,
          },
        };
      },
      filter: "agTextColumnFilter",
      floatingFilter: true,
    },
    {
      headerValueGetter: () => this.translocoService.translate("inventory-grid.fullfilled_by"),
      field: "fulfillmentChannel",
      filter: "agSetColumnFilter",
      floatingFilter: true,
      pinned: "left",
    },
    {
      headerValueGetter: () => this.translocoService.translate("common.title"),
      tooltipValueGetter: (params) => params.data.title,
      maxWidth: 400,
      field: "title",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      pinned: "left",
      hide: true,
    },
    {
      headerValueGetter: () => this.translocoService.translate("metrics.PRICE_title"),
      field: "price",
      cellStyle: { display: "flex", justifyContent: "end", alignItems: "end" },
      valueFormatter: (params: any) => {
        return this.currencyPipe.transform(params.value, this.currency(), "symbol", "1.2-2") ?? "";
      },
      pinned: "left",
      hide: true,
    },
    {
      headerValueGetter: () => this.translocoService.translate("inventory-grid.estimated_days_of_stock"),
      wrapHeaderText: true,
      autoHeaderHeight: true,
      colId: "estimatedDaysOfStock",
      valueGetter: (params: ValueGetterParams<InventoryStats>) => {
        if (!params.data) {
          return undefined;
        }
        return this.inventoryRules.execute(params.data.asin, params.data);
      },
      comparator: (valueA, valueB) => {
        return valueA.estimatedDaysOfStock - valueB.estimatedDaysOfStock;
      },
      filterValueGetter: (params: ValueGetterParams<InventoryStats>) => {
        if (!params.data) {
          return undefined;
        }
        return this.inventoryRules.execute(params.data?.asin, params.data)?.estimatedDaysOfStock;
      },
      cellClass: (params: CellClassParams<InventoryStats, RuleExecutionResult | undefined>) => {
        if (params.value === undefined) {
          return "bg-inherit";
        }
        const state = params.value?.detail;
        if (state == RuleExecutionResultEnum.LOW_STOCK) {
          return "bg-red-100";
        }
        if (state == RuleExecutionResultEnum.OK) {
          return "bg-green-100";
        }
        if (state == RuleExecutionResultEnum.WARNING) {
          return "bg-orange-100";
        }
        return "bg-inherit";
      },
      cellRendererSelector: (params: any) => {
        return {
          component: EstimatedDaysRendererComponent,
          params: {
            product: params.data,
            inventoryRules: this.inventoryRules,
            locale: this.locale(),
          },
        };
      },
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      filterParams: {
        defaultOption: "greaterThan",
      },
      pinned: "left",
    },
    {
      headerValueGetter: () => this.translocoService.translate("inventory-grid.pause_advertising_threshold"),
      wrapHeaderText: true,
      autoHeaderHeight: true,
      field: "pauseAdv",
      pinned: "left",
      cellStyle: { display: "flex", justifyContent: "end", alignItems: "end" },
      valueGetter: (params) => {
        return this.inventoryRules.asinInventoryRule.get(params.data.asin)?.advertisingPauseThreshold ?? 0;
      },
      cellRendererSelector: (params: any) => {
        return {
          component: PauseAdvRendererComponent,
          params: {
            product: params.data,
            inventoryRules: this.inventoryRules,
            locale: this.locale(),
            accountId: this.accountId,
            marketplace: this.marketplace,
            isReadOnly: this.isReadOnly(),
          },
        };
      },
    },
    ...this.metric,
  ];

  gridOptions: GridOptions = {
    ...gridConfig({ gridKey: "inventory", withFooterRow: false }),
    columnDefs: this.colDef,
    pagination: true,
    paginationPageSizeSelector: [10, 20, 50, 100],
    paginationPageSize: 20,
    isExternalFilterPresent: () => true,
    sideBar: SIDE_BAR_NO_PIVOT,
    onGridReady: (params) => {
      this.asinFilterService.filteredAsins$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filters) => {
        params.api.setGridOption("doesExternalFilterPass", (node: IRowNode<InventoryStats>) =>
          this.passFilter(node.data!, filters),
        );
      });
    },
  };

  constructor() {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        tap((am: AccountMarketplace) => {
          this.accountGroupName = am.accountGroupName ?? "";
          this.marketplace = am.marketplace;
          this.accountId = am.accountId;
        }),
        switchMap((am) => {
          return combineLatest([
            this.asinService.getInventoryRules(am.accountId, am.marketplace),
            this.asinService.getCatalogManagementMode(am.accountId, am.marketplace),
          ]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([rules, mode]) => {
        this.inventoryRules = rules ?? new InventoryRules([]);
        this.isManualManaging.set(mode == "manual");
        this.gridApi?.redrawRows();
      });
  }

  ngOnInit(): void {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        tap((am: AccountMarketplace) => {
          this.accountGroupName = am.accountGroupName ?? "";
        }),
        switchMap((am) =>
          combineLatest([
            this.spStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
            this.sbStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
            this.sdStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
            this.inventoryService.getSellerInventoryStats(am.accountId, am.marketplace),
          ]),
        ),
        untilDestroyed(this),
      )
      .subscribe(([spStrategiesPerAsin, sbStrategiesPerAsin, sdStrategiesPerAsin, data]) => {
        data = data.sort((stats1, stats2) => {
          return stats2.fulfillableQuantity - stats1.fulfillableQuantity;
        });
        this.dataSource.set(data);
        let lowInventoryNumber = 0;
        let lowInventoryPaused = 0;
        for (const stat of data) {
          const ruleExecution = this.inventoryRules?.execute(stat.asin, stat);
          if (ruleExecution?.detail == RuleExecutionResultEnum.LOW_STOCK) {
            lowInventoryNumber++;
          }
          if (ruleExecution?.shouldPauseAdvertising) {
            lowInventoryPaused++;
          }
        }
        this.lowInventoryNumber.set(lowInventoryNumber);
        this.lowInventoryPaused.set(lowInventoryPaused);
        this.strategies.clear();
        for (const [asin, strategies] of [...spStrategiesPerAsin, ...sbStrategiesPerAsin, ...sdStrategiesPerAsin]) {
          if (this.strategies.has(asin)) {
            this.strategies.get(asin)!.push(...strategies);
          } else {
            this.strategies.set(asin, strategies);
          }
        }
        this.gridApi?.setGridOption("columnDefs", [
          ...this.colDef,
          activeStrategiesColumn(this.translocoService, this.strategies, this.marketplace),
        ]);
      });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    restoreGridState("inventory", this.gridApi);
  }

  setupRuleForAllAsins(): void {
    this.modalService
      .openModal<AsinInventoryRulesModalData, AsinInventoryRulesOutput>(AsinInventoryRuleModalComponent, {
        modalTitle: this.translocoService.translate("asin-inventory-rules-modal.rule_title"),
        data: {
          accountId: this.accountId,
          marketplace: this.marketplace,
          warningMessage: this.translocoService.translate(
            "inventory-grid.warning_this_will_override_the_limit_already_defined_on_the_asins",
          ),
        },
      })
      .afterClosed()
      .pipe(
        switchMap((rule) => {
          if (rule === "delete") {
            return this.asinService.deleteAllAsinInventoryRules(this.accountId, this.marketplace);
          }
          return this.setRuleForAllAsins(rule);
        }),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(`Advertising pausing rule set for all ASINs`, "Inventory rule updated");
        },
        error: (error) => {
          this.toasterService.error(
            `Error when setting an advertising pausing rule for all ASINs: ${error}`,
            "Inventory rule update error",
          );
        },
      });
  }

  private setRuleForAllAsins(rule: InventoryRule): Observable<void> {
    const asins = new Set<string>();
    const rules: InventoryRule[] = [];
    for (const stat of this.dataSource()) {
      if (asins.has(stat.asin)) {
        continue;
      }
      asins.add(stat.asin);
      rules.push({
        accountId: this.accountId,
        marketplace: this.marketplace,
        asin: stat.asin,
        advertisingPauseThreshold: rule ? rule.advertisingPauseThreshold : -1,
        activateAdvertisingWhenInbound: rule ? rule.activateAdvertisingWhenInbound : false,
        onlyFba: rule ? rule.onlyFba : false,
      });
    }

    return this.asinService.setInventoryRulesInBulk(rules);
  }

  applyFilter(filters: Filter<AsinFilter>[]) {
    this.asinFilterService.selectFilters(filters);
    if (!this.gridApi?.isDestroyed()) {
      this.gridApi?.onFilterChanged();
    }
  }

  passFilter(stat: InventoryStats, filteredAsins: Set<string> | null) {
    let res = true;
    if (this.onlyLowInventory) {
      res = this.inventoryRules.execute(stat.asin, stat).detail == "low";
    }
    return res && AsinFilterService.asinMatchesFilter(stat.asin, filteredAsins);
  }

  toggleLowInventoryFilter() {
    this.onlyLowInventory = !this.onlyLowInventory;
    this.gridApi?.onFilterChanged();
  }

  downloadFile() {
    if (!this.dataSource().length) {
      this.toasterService.error("No data to export", "Export error");
      return;
    }
    this.csvExportService.exportCsv(
      {
        prefix: "inventory",
        accountGroupName: this.accountGroupName,
        marketplace: this.marketplace,
      },
      this.dataSource(),
      [
        simpleField("asin"),
        fieldExtractor("title", (stat) => stat.title ?? ""),
        constantField("currency", this.currency()),
        fieldExtractor("price", (stat) => stat.price?.toFixed(2) ?? "-"),
        fieldExtractor("strategies", (stat) =>
          (this.strategies.get(stat.asin) ?? []).filter((s) => s.state === StrategyStateEnum.ENABLED).length.toString(),
        ),
        metricField(ORDERS_7D),
        metricField(ORDERS_30D),
        fieldExtractor(
          "estimatedDaysOfStock",
          (stat) => this.inventoryRules.execute(stat.asin, stat)?.estimatedDaysOfStock?.toString() ?? "",
        ),
        metricField(FULFILLABLE_STOCK),
        metricField(FULFILLABLE_STOCK_VALUE, "availableValue"),
        metricField(FBM_STOCK),
        metricField(FBM_STOCK_VALUE, "availableFbmValue"),
        metricField(INBOUND_STOCK),
        metricField(INBOUND_STOCK_VALUE, "inboundValue"),
        metricField(RESERVED_STOCK),
        metricField(RESERVED_STOCK_VALUE, "reservedValue"),
        metricField(UNFULFILLABLE_STOCK),
        metricField(UNFULFILLABLE_STOCK_VALUE, "unfulfillableValue"),
        fieldExtractor(
          "pauseAdvertisingThreshold",
          (stat) =>
            this.inventoryRules.asinInventoryRule.get(stat.asin)?.advertisingPauseThreshold?.toFixed(0) ?? "None",
        ),
        fieldExtractor("isAdvertisingPaused", (stat) =>
          this.inventoryRules.execute(stat.asin, stat as InventoryStats).shouldPauseAdvertising ? "Yes" : "No",
        ),
      ],
    );
  }

  restoreDefaultColumns() {
    restoreDefaultColumns("inventory", this.gridApi!);
  }
}
